import { useEffect, useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';
import { IconButton } from '../components/IconButton';
import { IconAttach, IconBack, IconDownload, IconEdit, IconTrash } from '../components/icons';
import { Modal } from '../components/Modal';
import { useAuth } from '../auth/useAuth';
import { labelFor, listOptions, useConfig } from '../context/ConfigContext';
import { useConfirm } from '../context/ConfirmContext';
import { useToast } from '../context/ToastContext';
import { downloadFile } from '../utils/download';

interface Candidate {
  id: number;
  vacancy_id: number;
  full_name: string;
  email: string | null;
  phone: string | null;
  stage: string;
  notes: string | null;
  offered_salary: string | null;
  start_date: string | null;
  has_cv: boolean;
  cv_filename: string | null;
}

interface VacancyDetailRecord {
  id: number;
  title: string;
  org_unit_id: number;
  org_unit_name: string | null;
  position_id: number | null;
  position_title: string | null;
  status: 'open' | 'closed';
  closing_date: string | null;
  description: string | null;
  candidate_count: number;
  candidates: Candidate[];
}

interface OrgUnitOption {
  id: number;
  unit_name: string;
}

interface PositionOption {
  id: number;
  position_code: string;
  position_title: string;
}

const emptyVacancyForm = {
  title: '',
  org_unit_id: '',
  position_id: '',
  status: 'open' as 'open' | 'closed',
  closing_date: '',
  description: '',
};
const emptyCandidateForm = { full_name: '', email: '', phone: '', notes: '' };
const emptyCandidateEditForm = { full_name: '', email: '', phone: '', notes: '', stage: '' };

export function VacancyDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { can } = useAuth();
  const { lists } = useConfig();
  const confirmAction = useConfirm();
  const { showError } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadTargetRef = useRef<Candidate | null>(null);

  const [vacancy, setVacancy] = useState<VacancyDetailRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [orgUnits, setOrgUnits] = useState<OrgUnitOption[]>([]);
  const [positions, setPositions] = useState<PositionOption[]>([]);

  useEffect(() => {
    api.get<OrgUnitOption[]>('/org-units').then((res) => setOrgUnits(res.data));
    api.get<PositionOption[]>('/positions').then((res) => setPositions(res.data));
  }, []);

  const [editingVacancy, setEditingVacancy] = useState(false);
  const [vacancyForm, setVacancyForm] = useState(emptyVacancyForm);

  const [addingCandidate, setAddingCandidate] = useState(false);
  const [candidateForm, setCandidateForm] = useState(emptyCandidateForm);
  const [candidateCv, setCandidateCv] = useState<File | null>(null);

  const [editingCandidate, setEditingCandidate] = useState<Candidate | null>(null);
  const [candidateEditForm, setCandidateEditForm] = useState(emptyCandidateEditForm);

  const [errors, setErrors] = useState<Record<string, string[]>>({});

  const canCreate = can('hr.create');
  const canUpdate = can('hr.update');
  const canArchive = can('hr.archive');

  const load = async (): Promise<VacancyDetailRecord | null> => {
    setLoading(true);
    try {
      const res = await api.get<VacancyDetailRecord>(`/vacancies/${id}`);
      setVacancy(res.data);
      setError(null);
      return res.data;
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load vacancy.');
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const openEditVacancy = () => {
    if (!vacancy) return;
    setVacancyForm({
      title: vacancy.title,
      org_unit_id: String(vacancy.org_unit_id),
      position_id: vacancy.position_id ? String(vacancy.position_id) : '',
      status: vacancy.status,
      closing_date: vacancy.closing_date ?? '',
      description: vacancy.description ?? '',
    });
    setErrors({});
    setEditingVacancy(true);
  };

  const submitEditVacancy = async (e: FormEvent) => {
    e.preventDefault();
    if (!vacancy) return;
    try {
      await api.put(`/vacancies/${vacancy.id}`, {
        ...vacancyForm,
        position_id: vacancyForm.position_id || undefined,
        closing_date: vacancyForm.closing_date || undefined,
      });
      setEditingVacancy(false);
      load();
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const removeVacancy = async () => {
    if (!vacancy) return;
    if (!(await confirmAction(`Delete the "${vacancy.title}" vacancy and all its candidates?`))) return;
    try {
      await api.delete(`/vacancies/${vacancy.id}`);
      navigate('/hr/recruitment');
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  const openAddCandidate = () => {
    setCandidateForm(emptyCandidateForm);
    setCandidateCv(null);
    setErrors({});
    setAddingCandidate(true);
  };

  const submitAddCandidate = async (e: FormEvent) => {
    e.preventDefault();
    if (!vacancy) return;
    try {
      const data = new FormData();
      Object.entries(candidateForm).forEach(([key, value]) => data.append(key, value));
      data.append('vacancy_id', String(vacancy.id));
      if (candidateCv) data.append('cv', candidateCv);
      await api.post('/candidates', data, { headers: { 'Content-Type': 'multipart/form-data' } });
      setAddingCandidate(false);
      load();
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const openEditCandidate = (candidate: Candidate) => {
    setEditingCandidate(candidate);
    setCandidateEditForm({
      full_name: candidate.full_name,
      email: candidate.email ?? '',
      phone: candidate.phone ?? '',
      notes: candidate.notes ?? '',
      stage: candidate.stage,
    });
    setErrors({});
  };

  const submitEditCandidate = async (e: FormEvent) => {
    e.preventDefault();
    if (!editingCandidate) return;
    try {
      await api.put(`/candidates/${editingCandidate.id}`, candidateEditForm);
      setEditingCandidate(null);
      load();
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const changeStage = async (candidate: Candidate, stage: string) => {
    try {
      await api.put(`/candidates/${candidate.id}`, {
        full_name: candidate.full_name,
        email: candidate.email,
        phone: candidate.phone,
        notes: candidate.notes,
        stage,
      });
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Stage change failed.');
    }
  };

  const removeCandidate = async (candidate: Candidate) => {
    if (!(await confirmAction(`Delete the candidate record for ${candidate.full_name}?`))) return;
    try {
      await api.delete(`/candidates/${candidate.id}`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  // Keeps the open Edit modal in sync with the candidate's just-changed CV
  // state, since `editingCandidate` is a separate snapshot from `vacancy`.
  const refreshEditingCandidate = async (candidateId: number) => {
    const refreshed = await load();
    const match = refreshed?.candidates.find((c) => c.id === candidateId);
    if (match) setEditingCandidate(match);
  };

  const pickCv = (candidate: Candidate) => {
    uploadTargetRef.current = candidate;
    fileInputRef.current?.click();
  };

  const onCvSelected = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    const candidate = uploadTargetRef.current;
    if (!file || !candidate) return;

    const data = new FormData();
    data.append('cv', file);
    try {
      await api.post(`/candidates/${candidate.id}/cv`, data, { headers: { 'Content-Type': 'multipart/form-data' } });
      await refreshEditingCandidate(candidate.id);
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Upload failed.');
    }
  };

  const downloadCv = (candidate: Candidate) =>
    downloadFile(api, `/candidates/${candidate.id}/cv`, candidate.cv_filename ?? `${candidate.full_name}-cv`);

  const removeCv = async (candidate: Candidate) => {
    if (!(await confirmAction(`Remove the CV for ${candidate.full_name}?`))) return;
    try {
      await api.delete(`/candidates/${candidate.id}/cv`);
      await refreshEditingCandidate(candidate.id);
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Remove failed.');
    }
  };

  if (loading) return null;
  if (error || !vacancy) {
    return (
      <>
        <header className="page-header">
          <IconButton icon={IconBack} label="Back to list" onClick={() => navigate('/hr/recruitment')} />
        </header>
        <div className="card">
          <p className="error">{error ?? 'Vacancy not found.'}</p>
        </div>
      </>
    );
  }

  return (
    <>
      <header className="page-header">
        <div className="page-header-actions">
          <h1>{vacancy.title}</h1>
        </div>
        <div className="page-header-actions">
          <span className={`badge ${vacancy.status === 'open' ? 'badge-active' : 'badge-cancelled'}`}>{vacancy.status}</span>
          {canUpdate && (
            <button type="button" className="btn btn-ghost" onClick={openEditVacancy}>
              Edit
            </button>
          )}
          {canArchive && (
            <button type="button" className="btn btn-ghost" onClick={removeVacancy}>
              Delete
            </button>
          )}
          <IconButton icon={IconBack} label="Back to list" onClick={() => navigate('/hr/recruitment')} />
        </div>
      </header>

      {vacancy.description && <p>{vacancy.description}</p>}

      <div className="toolbar">
        <h2>Candidates</h2>
        {canCreate && (
          <button type="button" className="btn btn-primary" onClick={openAddCandidate}>
            + Add
          </button>
        )}
      </div>

      <input ref={fileInputRef} type="file" accept=".pdf,.doc,.docx" hidden onChange={onCvSelected} />

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Contact</th>
                <th>Stage</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {vacancy.candidates.length === 0 ? (
                <tr>
                  <td colSpan={4} className="muted center">
                    No candidates yet.
                  </td>
                </tr>
              ) : (
                vacancy.candidates.map((candidate) => (
                  <tr key={candidate.id}>
                    <td>
                      <strong>{candidate.full_name}</strong>
                    </td>
                    <td>
                      {candidate.email && <div>{candidate.email}</div>}
                      {candidate.phone && <div className="muted">{candidate.phone}</div>}
                    </td>
                    <td>
                      {canUpdate ? (
                        <select value={candidate.stage} onChange={(e) => changeStage(candidate, e.target.value)}>
                          {listOptions(lists, 'candidate_stage').map((item) => (
                            <option key={item.value} value={item.value}>
                              {item.label}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span className="badge badge-neutral">{labelFor(lists, 'candidate_stage', candidate.stage)}</span>
                      )}
                    </td>
                    <td className="right">
                      {canUpdate && (
                        <IconButton icon={IconEdit} label="Edit" onClick={() => openEditCandidate(candidate)} />
                      )}
                      {canArchive && (
                        <IconButton
                          icon={IconTrash}
                          label="Delete"
                          variant="danger-ghost"
                          onClick={() => removeCandidate(candidate)}
                        />
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editingVacancy && (
        <Modal title="Edit vacancy" onClose={() => setEditingVacancy(false)}>
          <form onSubmit={submitEditVacancy} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            <label className="span-2">
              Title *
              <input
                type="text"
                value={vacancyForm.title}
                onChange={(e) => setVacancyForm({ ...vacancyForm, title: e.target.value })}
                required
              />
              {errors.title && <small className="error">{errors.title[0]}</small>}
            </label>
            <label>
              Org Unit *
              <select
                value={vacancyForm.org_unit_id}
                onChange={(e) => setVacancyForm({ ...vacancyForm, org_unit_id: e.target.value })}
                required
              >
                <option value="">Select…</option>
                {orgUnits.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.unit_name}
                  </option>
                ))}
              </select>
              {errors.org_unit_id && <small className="error">{errors.org_unit_id[0]}</small>}
            </label>
            <label>
              Position
              <select
                value={vacancyForm.position_id}
                onChange={(e) => setVacancyForm({ ...vacancyForm, position_id: e.target.value })}
              >
                <option value="">None</option>
                {positions.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.position_code} — {p.position_title}
                  </option>
                ))}
              </select>
              {errors.position_id && <small className="error">{errors.position_id[0]}</small>}
            </label>
            <label>
              Status *
              <select
                value={vacancyForm.status}
                onChange={(e) => setVacancyForm({ ...vacancyForm, status: e.target.value as 'open' | 'closed' })}
              >
                <option value="open">Open</option>
                <option value="closed">Closed</option>
              </select>
            </label>
            <label>
              Closing Date
              <input
                type="date"
                value={vacancyForm.closing_date}
                onChange={(e) => setVacancyForm({ ...vacancyForm, closing_date: e.target.value })}
              />
              {errors.closing_date && <small className="error">{errors.closing_date[0]}</small>}
            </label>
            <label className="span-2">
              Description
              <textarea
                rows={3}
                value={vacancyForm.description}
                onChange={(e) => setVacancyForm({ ...vacancyForm, description: e.target.value })}
              />
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setEditingVacancy(false)}>
                Cancel
              </button>
              <button className="btn btn-primary">Save</button>
            </div>
          </form>
        </Modal>
      )}

      {addingCandidate && (
        <Modal title="Add candidate" onClose={() => setAddingCandidate(false)}>
          <form onSubmit={submitAddCandidate} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            <label className="span-2">
              Full name *
              <input
                type="text"
                value={candidateForm.full_name}
                onChange={(e) => setCandidateForm({ ...candidateForm, full_name: e.target.value })}
                required
              />
              {errors.full_name && <small className="error">{errors.full_name[0]}</small>}
            </label>
            <label>
              Email
              <input
                type="email"
                value={candidateForm.email}
                onChange={(e) => setCandidateForm({ ...candidateForm, email: e.target.value })}
              />
              {errors.email && <small className="error">{errors.email[0]}</small>}
            </label>
            <label>
              Phone
              <input
                type="text"
                value={candidateForm.phone}
                onChange={(e) => setCandidateForm({ ...candidateForm, phone: e.target.value })}
              />
            </label>
            <label className="span-2">
              CV
              <input type="file" accept=".pdf,.doc,.docx" onChange={(e) => setCandidateCv(e.target.files?.[0] ?? null)} />
              {errors.cv && <small className="error">{errors.cv[0]}</small>}
            </label>
            <label className="span-2">
              Notes
              <textarea
                rows={3}
                value={candidateForm.notes}
                onChange={(e) => setCandidateForm({ ...candidateForm, notes: e.target.value })}
              />
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setAddingCandidate(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" disabled={!candidateForm.full_name}>
                Save
              </button>
            </div>
          </form>
        </Modal>
      )}

      {editingCandidate && (
        <Modal title={`Edit ${editingCandidate.full_name}`} onClose={() => setEditingCandidate(null)}>
          <form onSubmit={submitEditCandidate} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            <label className="span-2">
              Full name *
              <input
                type="text"
                value={candidateEditForm.full_name}
                onChange={(e) => setCandidateEditForm({ ...candidateEditForm, full_name: e.target.value })}
                required
              />
              {errors.full_name && <small className="error">{errors.full_name[0]}</small>}
            </label>
            <label>
              Email
              <input
                type="email"
                value={candidateEditForm.email}
                onChange={(e) => setCandidateEditForm({ ...candidateEditForm, email: e.target.value })}
              />
              {errors.email && <small className="error">{errors.email[0]}</small>}
            </label>
            <label>
              Phone
              <input
                type="text"
                value={candidateEditForm.phone}
                onChange={(e) => setCandidateEditForm({ ...candidateEditForm, phone: e.target.value })}
              />
            </label>
            <label className="span-2">
              Stage *
              <select
                value={candidateEditForm.stage}
                onChange={(e) => setCandidateEditForm({ ...candidateEditForm, stage: e.target.value })}
                required
              >
                {listOptions(lists, 'candidate_stage').map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
              {errors.stage && <small className="error">{errors.stage[0]}</small>}
            </label>
            <label className="span-2">
              CV
              {editingCandidate.has_cv ? (
                <div>
                  <IconButton icon={IconDownload} label="Download CV" onClick={() => downloadCv(editingCandidate)} />
                  <IconButton
                    icon={IconTrash}
                    label="Remove CV"
                    variant="danger-ghost"
                    onClick={() => removeCv(editingCandidate)}
                  />
                </div>
              ) : (
                <div>
                  <IconButton icon={IconAttach} label="Attach CV" onClick={() => pickCv(editingCandidate)} />
                </div>
              )}
            </label>
            <label className="span-2">
              Notes
              <textarea
                rows={3}
                value={candidateEditForm.notes}
                onChange={(e) => setCandidateEditForm({ ...candidateEditForm, notes: e.target.value })}
              />
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setEditingCandidate(null)}>
                Cancel
              </button>
              <button className="btn btn-primary">Save</button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
